import React, { useState, useRef, useEffect } from "react";
import { io } from "socket.io-client";
import { SOCKET_URL, API_URL } from "../config/apiConfig";
import "./TrackingSimulator.css";

/**
 * TrackingSimulator – Developer / Demo tool
 * Accessible at /track-demo
 *
 * Simulates a driver moving along a route so you can test the
 * LiveTracking page without a real GPS device.
 */

// Sample routes around major Indian cities
const DEMO_ROUTES = {
  delhi: {
    name: "Delhi: CP → Airport",
    waypoints: [
      { lat: 28.6315, lng: 77.2167 },
      { lat: 28.6273, lng: 77.2090 },
      { lat: 28.6200, lng: 77.2050 },
      { lat: 28.6100, lng: 77.1950 },
      { lat: 28.5982, lng: 77.1912 },
      { lat: 28.5850, lng: 77.1800 },
      { lat: 28.5800, lng: 77.1650 },
      { lat: 28.5665, lng: 77.1031 },
    ],
  },
  mumbai: {
    name: "Mumbai: CST → BKC",
    waypoints: [
      { lat: 18.9398, lng: 72.8355 },
      { lat: 18.9432, lng: 72.8370 },
      { lat: 18.9500, lng: 72.8420 },
      { lat: 18.9600, lng: 72.8500 },
      { lat: 18.9700, lng: 72.8580 },
      { lat: 18.9782, lng: 72.8640 },
    ],
  },
  bangalore: {
    name: "Bangalore: MG Road → Airport",
    waypoints: [
      { lat: 12.9752, lng: 77.6094 },
      { lat: 12.9800, lng: 77.6150 },
      { lat: 12.9900, lng: 77.6200 },
      { lat: 13.0100, lng: 77.6300 },
      { lat: 13.0300, lng: 77.5700 },
      { lat: 13.0800, lng: 77.4800 },
      { lat: 13.1989, lng: 77.7068 },
    ],
  },
};

export default function TrackingSimulator() {
  const [rideId, setRideId]               = useState("TEST-RIDE-001");
  const [route, setRoute]                 = useState("delhi");
  const [intervalMs, setIntervalMs]       = useState(1500);
  const [isRunning, setIsRunning]         = useState(false);
  const [status, setStatus]               = useState("idle");   // idle | running | done | error
  const [log, setLog]                     = useState([]);
  const [currentStep, setCurrentStep]     = useState(0);
  const [socketConnected, setSocketConnected] = useState(false);

  const socketRef   = useRef(null);
  const timerRef    = useRef(null);
  const stepRef     = useRef(0);
  const logEndRef   = useRef(null);

  const waypoints = DEMO_ROUTES[route].waypoints;

  const addLog = (msg, type = "info") =>
    setLog((prev) => [...prev, { msg, type, ts: new Date().toLocaleTimeString() }]);

  /* ── Socket connection ─────────────────────────────────────────── */
  useEffect(() => {
    const socket = io(SOCKET_URL, {
      transports: ["websocket", "polling"],
      reconnectionAttempts: 5,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setSocketConnected(true);
      addLog(`Socket connected (id: ${socket.id})`, "success");
    });
    socket.on("disconnect", () => {
      setSocketConnected(false);
      addLog("Socket disconnected", "warn");
    });
    socket.on("rideRoomJoined", (data) =>
      addLog(`Joined ride room: ${data.room}`, "success")
    );

    return () => {
      socket.disconnect();
    };
  }, []);

  /* ── Auto scroll log ───────────────────────────────────────────── */
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [log]);

  /* ── Start simulation ──────────────────────────────────────────── */
  const startSimulation = () => {
    if (!rideId.trim()) return addLog("Please enter a Ride ID.", "error");
    if (!socketRef.current?.connected) return addLog("Socket not connected.", "error");

    stepRef.current = 0;
    setCurrentStep(0);
    setLog([]);
    setStatus("running");
    setIsRunning(true);

    // Join the ride room so we can verify our own events echo back
    socketRef.current.emit("joinRideRoom", rideId);
    addLog(`Joined ride room for: ${rideId}`, "success");
    addLog(`Starting simulation over ${waypoints.length} waypoints…`, "info");

    const emitNext = () => {
      const idx = stepRef.current;
      if (idx >= waypoints.length) {
        clearInterval(timerRef.current);
        setIsRunning(false);
        setStatus("done");
        addLog("✅ Simulation complete!", "success");
        return;
      }

      const { lat, lng } = waypoints[idx];
      socketRef.current.emit("locationUpdate", { rideId, lat, lng });
      addLog(`📍 Step ${idx + 1}/${waypoints.length} → lat:${lat.toFixed(5)}, lng:${lng.toFixed(5)}`, "info");
      stepRef.current++;
      setCurrentStep(stepRef.current);
    };

    emitNext(); // fire immediately
    timerRef.current = setInterval(emitNext, intervalMs);
  };

  const stopSimulation = () => {
    clearInterval(timerRef.current);
    setIsRunning(false);
    setStatus("stopped");
    addLog("⏹ Simulation stopped.", "warn");
  };

  const resetSimulation = () => {
    clearInterval(timerRef.current);
    setIsRunning(false);
    setStatus("idle");
    setCurrentStep(0);
    stepRef.current = 0;
    setLog([]);
  };

  const trackingUrl = `${window.location.origin}/track/${rideId}`;
  const progress = waypoints.length ? Math.round((currentStep / waypoints.length) * 100) : 0;

  return (
    <div className="sim-root">
      <header className="sim-header">
        <span className="sim-brand">🚗 Live Tracking Simulator</span>
        <span className={`sim-badge ${socketConnected ? "sim-badge-on" : "sim-badge-off"}`}>
          <span className="sim-pulse" />
          {socketConnected ? "Socket Connected" : "Socket Disconnected"}
        </span>
      </header>

      <div className="sim-body">
        {/* ── Controls ─────────────────────────────────────────────── */}
        <div className="sim-controls">
          <h2 className="sim-section-title">Simulator Settings</h2>

          <label className="sim-label">
            Ride ID
            <input
              id="sim-ride-id"
              className="sim-input"
              value={rideId}
              onChange={(e) => setRideId(e.target.value)}
              placeholder="e.g. UR-001234"
              disabled={isRunning}
            />
          </label>

          <label className="sim-label">
            Demo Route
            <select
              id="sim-route-select"
              className="sim-input"
              value={route}
              onChange={(e) => setRoute(e.target.value)}
              disabled={isRunning}
            >
              {Object.entries(DEMO_ROUTES).map(([key, r]) => (
                <option key={key} value={key}>{r.name}</option>
              ))}
            </select>
          </label>

          <label className="sim-label">
            Update Interval (ms)
            <input
              id="sim-interval"
              className="sim-input"
              type="number"
              min="500"
              max="10000"
              step="500"
              value={intervalMs}
              onChange={(e) => setIntervalMs(Number(e.target.value))}
              disabled={isRunning}
            />
          </label>

          {/* Progress bar */}
          <div className="sim-progress-wrap">
            <div className="sim-progress-label">
              <span>Progress</span>
              <span>{currentStep} / {waypoints.length} waypoints</span>
            </div>
            <div className="sim-progress-bar">
              <div
                className="sim-progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Action buttons */}
          <div className="sim-actions">
            {!isRunning ? (
              <button
                id="sim-start-btn"
                className="sim-btn sim-btn-start"
                onClick={startSimulation}
                disabled={!socketConnected}
              >
                ▶ Start Simulation
              </button>
            ) : (
              <button
                id="sim-stop-btn"
                className="sim-btn sim-btn-stop"
                onClick={stopSimulation}
              >
                ⏹ Stop
              </button>
            )}
            <button
              id="sim-reset-btn"
              className="sim-btn sim-btn-reset"
              onClick={resetSimulation}
              disabled={isRunning}
            >
              ↺ Reset
            </button>
          </div>

          {/* Tracking link */}
          <div className="sim-link-wrap">
            <p className="sim-link-label">🔗 Open tracking page to see live updates:</p>
            <div className="sim-link-row">
              <a
                id="sim-track-link"
                href={trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="sim-link"
              >
                {trackingUrl}
              </a>
              <button
                id="sim-copy-link"
                className="sim-btn sim-btn-copy"
                onClick={() => {
                  navigator.clipboard.writeText(trackingUrl);
                  document.getElementById("sim-copy-link").textContent = "✅";
                  setTimeout(() => {
                    const btn = document.getElementById("sim-copy-link");
                    if (btn) btn.textContent = "📋";
                  }, 2000);
                }}
              >
                📋
              </button>
            </div>
          </div>

          {/* How to test */}
          <div className="sim-howto">
            <h3>How to Test</h3>
            <ol>
              <li>Enter a Ride ID (or use the default).</li>
              <li>Open the tracking link above in another tab.</li>
              <li>Click <strong>▶ Start Simulation</strong> here.</li>
              <li>Watch the car marker move smoothly on the map!</li>
            </ol>
          </div>
        </div>

        {/* ── Log ──────────────────────────────────────────────────── */}
        <div className="sim-log-panel">
          <div className="sim-log-header">
            <h2 className="sim-section-title">Event Log</h2>
            <span className={`sim-status-tag sim-status-${status}`}>
              {status.toUpperCase()}
            </span>
          </div>
          <div className="sim-log">
            {log.length === 0 && (
              <p className="sim-log-empty">Waiting for simulation to start…</p>
            )}
            {log.map((entry, i) => (
              <div key={i} className={`sim-log-entry sim-log-${entry.type}`}>
                <span className="sim-log-ts">{entry.ts}</span>
                <span className="sim-log-msg">{entry.msg}</span>
              </div>
            ))}
            <div ref={logEndRef} />
          </div>
        </div>
      </div>
    </div>
  );
}
